import { useChartStore } from '@/stores/chartStore';
import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
import { getRegexEscapedString } from '@/utils/getRegexEscapedString';
import { getPeriodOptionFromPeriodValue } from '@/enums/period-options';
import { ColorScheme, COLOR_SCHEME_CUSTOM, getColorSchemeObjFromColorSchemeValue } from '@/enums/color-schemes';
import {
  CHART_TYPE_OPTION_HEIKIN_ASHI,
  CHART_TYPE_OPTION_THREE_LINE_BREAK,
  CHART_TYPE_OPTION_KAGI,
  CHART_TYPE_OPTION_RENKO,
  CHART_TYPE_OPTION_PERFORMANCE_PERCENT,
  CHART_TYPE_OPTION_PERF_CHART,
  getChartTypeObjFromChartTypeValue
} from '@/enums/chart-type-options';
import { DATE_TIME_AXIS, ICHIMOKU_CLOUD, ICHIMOKU_CLOUD_FULL, PRICE, PRICE_LABELS, PRICE_PERFORMANCE, PRICE_SAME_SCALE, VOLUME, VOLUME_BY_PRICE, NONE, getIndicatorOrOverlayObjFromIndicatorOrOverlayName } from '@/enums/indicators-and-overlays';
import { OVERLAY_STYLE_AREA, getOverlayStyleObjFromOverlayStyleTitle } from '@/enums/overlay-styles';
import { OPACITY_OPTION_ZERO_POINT_FIVE, getOpacityObjFromOpacityTitle } from '@/enums/opacity-options';
import { IndicatorPosition, INDICATOR_POSITION_ABOVE, INDICATOR_POSITION_BELOW, INDICATOR_POSITION_BEHIND_INDICATOR, INDICATOR_POSITION_BEHIND_PRICE } from '@/enums/indicator-positions';
import { INDICATOR_HEIGHT_OPTION_AUTO, getIndicatorHeightObjFromIndicatorHeightTitle } from '@/enums/indicator-height-options';
import { CHART_SETTINGS_VOLUME_MENU_OPTION_OVERLAY, CHART_SETTINGS_VOLUME_MENU_OPTION_SEPARATE } from '@/enums/chart-settings-volume-menu-options';
import { CHART_SIZE_MENU_OPTION_CUSTOM, CHART_SIZE_MENU_OPTION_PORTRAIT, CHART_SIZE_MENU_OPTION_LANDSCAPE, getChartSizeObjFromChartSizeTitle } from '@/enums/chart-size-menu-options';
import { getChartGridObjFromChartGridTitle } from '@/enums/chart-grid-options';
import { LEGEND_OPTION_OFF, LEGEND_OPTION_MINIMAL, LEGEND_OPTION_VERBOSE, LEGEND_OPTIONS_ARRAY } from '@/enums/legend-options';
import { getPriceIndicatorStyleObjFromPriceIndicatorStyleTitle } from '@/enums/price-indicator-style-options';
import { getRangeObjFromRangeTitle } from '@/enums/range-options';
import { Indicator } from '@/interfaces/chart';

const SHARPCHART = 'sharpchart';
const PANEL = 'panel';
const LAYER = 'layer';
const SCALE = 'scale';
const DATASET = 'dataset';
const STYLE = 'style';
const PROPERTY = 'property';
type XmlNodeType = typeof SHARPCHART | typeof PANEL | typeof LAYER | typeof SCALE | typeof DATASET | typeof STYLE | typeof PROPERTY;
interface XmlNodeAttribute {
  key: string;
  value: string;
};
interface XmlNode {
  type: XmlNodeType;
  attributes: XmlNodeAttribute[];
  children: XmlNode[];
};
const DEFAULT_INDICATOR_PANEL_HEIGHT = 0.35;

function createAttribute(key: string, value: string): XmlNodeAttribute {
  return {
    key: key.trim(),
    value: value.trim()
  };
}

function createNode(nodeType: XmlNodeType): XmlNode {
  return {
    type: nodeType,
    attributes: [],
    children: []
  };
}

function addExistingChildNode(parent: XmlNode, child: XmlNode): XmlNode {
  parent.children.push(child);

  return child;
}

function addNewChildNode(parent: XmlNode, childType: XmlNodeType): XmlNode {
  const childNode = createNode(childType);

  return addExistingChildNode(parent, childNode);
}

function addPropertyChildNode(parentNode: XmlNode, propertyName: string, propertyValue: string): XmlNode {
  const propertyChildNode = addNewChildNode(parentNode, PROPERTY);

  addAttribute(propertyChildNode, 'name', propertyName);
  addAttribute(propertyChildNode, 'value', propertyValue);

  return propertyChildNode;
}

function addAttribute(node: XmlNode, attributeKey: string, attributeValue: string): void {
  if (attributeValue === '') {
    return;
  }

  const attribute = createAttribute(attributeKey, attributeValue);

  node.attributes.push(attribute);
}

function replaceSymbolWithString(inputString: string, symbolToRemove: string, stringToAdd: string): string {
  const regexEscapedSymbolToRemove = getRegexEscapedString(symbolToRemove);

  // the following characters are considered delimiters in this function: colons, hyphens, underscores, or commas
  // i.e. characters that join compound/special stockcharts symbols (such as ratio, difference, inverted, or unadjusted symbols) OR commas (which separate individual indicator/overlay parameters)
  const pattern = `(^|[:\\-,_])(${regexEscapedSymbolToRemove})([:\\-,_]|$)`; // three regex capturing groups: start of string or delimiter, stringToRemove, end of string or delimiter
  const regex = new RegExp(pattern, 'ig');
  const inputStringParts = inputString.split(regex).filter(s => !!s);
  let result = '';

  for (const inputStringPart of inputStringParts) {
    // Replace occurrences of the symbolToRemove with the stringToAdd only if it is surrounded by delimiters or at boundaries
    result += inputStringPart.replace(regex, (match, firstRegexCapturingGroupResult, secondRegexCapturingGroupResult, thirdRegexCapturingGroupResult) => {
      return `${firstRegexCapturingGroupResult}${stringToAdd}${thirdRegexCapturingGroupResult}`;
    });
  }

  return result;
}

function getUpDownColorData(colorScheme: ColorScheme): { upColorSwatch: string, downColorSwatch: string, upColorRgb: string, downColorRgb: string } {
  const chartStore = useChartStore();
  const upColorSwatchNumber = colorScheme.sharpCharts2OverlaySwatches.findIndex(swatch => swatch.upDownColorOption === chartStore.upColor);
  let upColorSwatch = upColorSwatchNumber.toString();
  const downColorSwatchNumber = colorScheme.sharpCharts2OverlaySwatches.findIndex(swatch => swatch.upDownColorOption === chartStore.downColor);
  let downColorSwatch = downColorSwatchNumber.toString();
  let upColorRgb = colorScheme.defaultSharpCharts2OverlayLineSwatchUpColor;
  let downColorRgb = colorScheme.defaultSharpCharts2OverlayLineSwatchDownColor;

  if (upColorSwatchNumber > -1) {
    upColorRgb = colorScheme.sharpCharts2OverlaySwatches[upColorSwatchNumber].lineColor;
  } else {
    upColorSwatch = 'auto';
  }

  if (downColorSwatchNumber > -1) {
    downColorRgb = colorScheme.sharpCharts2OverlaySwatches[downColorSwatchNumber].lineColor;
  } else {
    downColorSwatch = 'auto';
  }

  return {
    upColorSwatch,
    downColorSwatch,
    upColorRgb,
    downColorRgb
  };
}

function calculateSharpCharts2ChartHeight(): number {
  const chartStore = useChartStore();

  const chartSizeMenuOption = getChartSizeObjFromChartSizeTitle(chartStore.chartSize);

  if (chartSizeMenuOption === CHART_SIZE_MENU_OPTION_CUSTOM) {
    return chartStore.customHeight;
  }

  let height = chartSizeMenuOption.height;

  if (
    chartSizeMenuOption === CHART_SIZE_MENU_OPTION_PORTRAIT ||
    chartSizeMenuOption === CHART_SIZE_MENU_OPTION_LANDSCAPE
  ) {
    return height;
  }

  let totalIndicatorPanelsHeight = 0;

  if (chartStore.chartSettingsVolumeMenuValue === CHART_SETTINGS_VOLUME_MENU_OPTION_SEPARATE) {
    totalIndicatorPanelsHeight += DEFAULT_INDICATOR_PANEL_HEIGHT;
  }

  for (const indicator of chartStore.sortedIndicators) {
    if (
      !indicator?.indicatorName ||
      indicator.indicatorName === NONE ||
      indicator.indicatorName === DATE_TIME_AXIS.name ||
      ![INDICATOR_POSITION_ABOVE.title, INDICATOR_POSITION_BELOW.title].includes(indicator.indicatorPosition)
    ) {
      continue;
    }

    if (indicator.indicatorHeight === INDICATOR_HEIGHT_OPTION_AUTO.title) {
      totalIndicatorPanelsHeight += DEFAULT_INDICATOR_PANEL_HEIGHT;
      continue;
    }

    totalIndicatorPanelsHeight += Number(indicator.indicatorHeight);
  }

  height = Math.floor(height * (1 + totalIndicatorPanelsHeight));

  if (chartStore.fullQuote) {
    height += 73;
  }

  return height;
}

function addChartSettingsToSharpChartXMLNode(sharpChartXMLNode: XmlNode, colorScheme: ColorScheme): void {
  const chartStore = useChartStore();

  addPropertyChildNode(sharpChartXMLNode, 'chart.skin', colorScheme.sharpCharts2Id);

  const useMainChartSymbolAsBaselinePropertyValue = chartStore.useMainChartSymbolAsBaseline ? 'perf' : 'pct';

  addPropertyChildNode(sharpChartXMLNode, 'data.compare.mode', useMainChartSymbolAsBaselinePropertyValue);

  const chartSizeMenuOption = getChartSizeObjFromChartSizeTitle(chartStore.chartSize);
  const chartWidth = chartSizeMenuOption === CHART_SIZE_MENU_OPTION_CUSTOM ? chartStore.customWidth : chartSizeMenuOption.width;
  const chartHeight = calculateSharpCharts2ChartHeight();

  addPropertyChildNode(sharpChartXMLNode, 'ui.chart.size', chartSizeMenuOption.sharpCharts2Id);
  addPropertyChildNode(sharpChartXMLNode, 'chart.width', chartWidth.toString());
  addPropertyChildNode(sharpChartXMLNode, 'chart.height', chartHeight.toString());
  addPropertyChildNode(sharpChartXMLNode, 'chart.svg', 'false');

  if (colorScheme === COLOR_SCHEME_CUSTOM) {
    const customColorSchemeBackgroundColor = chartStore.customColorSchemeBackgroundColor.startsWith('#') ? chartStore.customColorSchemeBackgroundColor.slice(1): chartStore.customColorSchemeBackgroundColor;
    const customColorSchemeGridColor = chartStore.customColorSchemeGridColor.startsWith('#') ? chartStore.customColorSchemeGridColor.slice(1): chartStore.customColorSchemeGridColor;
    const customColorSchemeTextColor = chartStore.customColorSchemeTextColor.startsWith('#') ? chartStore.customColorSchemeTextColor.slice(1): chartStore.customColorSchemeTextColor;
    const customColorSchemeIndicatorColor = chartStore.customColorSchemeIndicatorColor.startsWith('#') ? chartStore.customColorSchemeIndicatorColor.slice(1): chartStore.customColorSchemeIndicatorColor;

    addPropertyChildNode(sharpChartXMLNode, 'custom.color.background', customColorSchemeBackgroundColor);
    addPropertyChildNode(sharpChartXMLNode, 'custom.color.grid', customColorSchemeGridColor);
    addPropertyChildNode(sharpChartXMLNode, 'custom.color.text', customColorSchemeTextColor);
    addPropertyChildNode(sharpChartXMLNode, 'custom.color.indicator', customColorSchemeIndicatorColor);
  }

  const { upColorSwatch, downColorSwatch, upColorRgb, downColorRgb } = getUpDownColorData(colorScheme);

  if (upColorRgb !== downColorRgb) {
    addPropertyChildNode(sharpChartXMLNode, 'palette.upPrice', upColorRgb);
    addPropertyChildNode(sharpChartXMLNode, 'palette.downPrice', downColorRgb);

    if (upColorSwatch !== 'auto' || downColorSwatch !== 'auto') {
      addPropertyChildNode(sharpChartXMLNode, 'palette.upVol', upColorRgb);
      addPropertyChildNode(sharpChartXMLNode, 'palette.downVol', downColorRgb);
    }
  }

  const chartGridOption = getChartGridObjFromChartGridTitle(chartStore.chartGrid);

  addPropertyChildNode(sharpChartXMLNode, 'grid.density.horiz', chartGridOption.sharpCharts2Id);
  addPropertyChildNode(sharpChartXMLNode, 'grid.density.vert', chartGridOption.sharpCharts2Id);

  if (chartGridOption.isDark && colorScheme !== COLOR_SCHEME_CUSTOM) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.alpha.lines.major.1', colorScheme.sharpCharts2GridAlphaLinesMajorOneValue);
    addPropertyChildNode(sharpChartXMLNode, 'grid.alpha.lines.major.2', colorScheme.sharpCharts2GridAlphaLinesMajorTwoValue);
    addPropertyChildNode(sharpChartXMLNode, 'grid.alpha.lines.minor', colorScheme.sharpCharts2GridAlphaLinesMinorValue);
  }

  const periodOption = getPeriodOptionFromPeriodValue(chartStore.period);
  const rangeOption = getRangeObjFromRangeTitle(chartStore.range);

  addPropertyChildNode(sharpChartXMLNode, 'data.type', periodOption.sharpCharts2Id);

  if (rangeOption.useBarAndGap) {
    addPropertyChildNode(sharpChartXMLNode, 'data.barWidth', chartStore.rangeBar);
    addPropertyChildNode(sharpChartXMLNode, 'data.gapWidth', chartStore.rangeGap);
  } else if (rangeOption.useNumberOfDays) {
    const numberOfDays = Number(chartStore.rangeDays) + (30 * Number(chartStore.rangeMonths)) + (365 * Number(chartStore.rangeYears));
    addPropertyChildNode(sharpChartXMLNode, 'data.days', numberOfDays.toString());
  } else if (rangeOption.useStartDateAndEndDate) {
    addPropertyChildNode(sharpChartXMLNode, 'data.start', chartStore.rangeStartDate);
    addPropertyChildNode(sharpChartXMLNode, 'data.end', chartStore.rangeEndDate);
  }

  const rangeExtraBars = chartStore.rangeExtraBars === 0 ? '' : chartStore.rangeExtraBars.toString();

  addPropertyChildNode(sharpChartXMLNode, 'data.extraperiods', rangeExtraBars);

  addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'price');
  addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'false');

  const legendOption = LEGEND_OPTIONS_ARRAY.find(option => option.title === chartStore.legends);

  if (legendOption === LEGEND_OPTION_OFF) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'false');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'false');
  } else if (legendOption === LEGEND_OPTION_MINIMAL) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'price');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'false');
  } else if (legendOption === LEGEND_OPTION_VERBOSE) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.companynames', 'true');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'true');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'true');
  } else {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'true');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'true');
  }

  addPropertyChildNode(sharpChartXMLNode, 'grid.show.streaminglabels', chartStore.yAxisLabels.toString());
  addPropertyChildNode(sharpChartXMLNode, 'chart.show.zoomthumbnail', chartStore.zoomThumbnail.toString());
  addPropertyChildNode(sharpChartXMLNode, 'chart.show.extendedhours', chartStore.extendedHours.toString());
  addPropertyChildNode(sharpChartXMLNode, 'data.symbol', chartStore.symbol);

  const chartTypeDataset = addNewChildNode(sharpChartXMLNode, DATASET);

  addAttribute(chartTypeDataset, 'id', 'master');
  addAttribute(chartTypeDataset, 'symbol', 'data.symbol');

  if (chartStore.chartType === CHART_TYPE_OPTION_THREE_LINE_BREAK.value) {
    addAttribute(chartTypeDataset, 'filter', 'TLB, 3, close');
  } else if (chartStore.chartType === CHART_TYPE_OPTION_KAGI.value) {
    addAttribute(chartTypeDataset, 'filter', `KAGI, ${chartStore.kagiReversal}, ${chartStore.kagiUnit}, ${chartStore.kagiPriceField}`);
  } else if (chartStore.chartType === CHART_TYPE_OPTION_RENKO.value) {
    addAttribute(chartTypeDataset, 'filter', `RENKO, ${chartStore.renkoBoxSize}, ${chartStore.renkoUnit}, ${chartStore.renkoPriceField}`);
  } else if (chartStore.chartType === CHART_TYPE_OPTION_HEIKIN_ASHI.value) {
    addAttribute(chartTypeDataset, 'filter', 'HEIKIN');
  }

  for (const ichimokuCloudOverlay of chartStore.ichimokuCloudOverlays) {
    let kijunSenPeriod = '26';
    const overlayParameters = ichimokuCloudOverlay.parameters.split(',');

    if (overlayParameters.length >= 2) {
      kijunSenPeriod = overlayParameters[1];
    }

    addPropertyChildNode(sharpChartXMLNode, 'data.ichimoku_extra', kijunSenPeriod);
  }

  const sigDataset = addNewChildNode(sharpChartXMLNode, DATASET);

  addAttribute(sigDataset, 'id', 'sig');
  addAttribute(sigDataset, 'class', 'SIGCHG');

  if (chartStore.solidCandles) {
    addAttribute(sigDataset, 'args', '1,0,open');
  } else {
    addAttribute(sigDataset, 'args', '1,0');
  }

  addAttribute(sigDataset, 'parent', 'price');

  const headerPanel = addNewChildNode(sharpChartXMLNode, PANEL);
  const headerLayer = addNewChildNode(headerPanel, LAYER);
  const headerDataset = addNewChildNode(headerLayer, DATASET);

  addAttribute(headerDataset, 'id', 'head');
  addAttribute(headerDataset, 'class', 'HEADER');
  addAttribute(headerDataset, 'parent', 'price');

  if (chartStore.fullQuote) {
    const fullQuotePanel = addNewChildNode(sharpChartXMLNode, PANEL);
    const fullQuoteLayer = addNewChildNode(fullQuotePanel, LAYER);
    const fullQuoteScale = addNewChildNode(fullQuoteLayer, SCALE);
    const fullQuoteDataset = addNewChildNode(fullQuoteLayer, DATASET);

    addAttribute(fullQuoteLayer, 'alpha', '1.0');
    addAttribute(fullQuoteScale, 'type', 'right');
    addAttribute(fullQuoteDataset, 'id', 'fullquote');
    addAttribute(fullQuoteDataset, 'class', 'BIGQ');
    addAttribute(fullQuoteDataset, 'parent', 'price');
  }
}

function addPerfChartSettingsToSharpChartXMLNode(sharpChartXMLNode: XmlNode, colorScheme: ColorScheme): void {
  const chartStore = useChartStore();
  const { getPerfChartSymbolsString } = usePerfChartSymbols();

  addPropertyChildNode(sharpChartXMLNode, 'data.symbol', chartStore.symbol);
  addPropertyChildNode(sharpChartXMLNode, 'chart.skin', colorScheme.sharpCharts2Id);
  addPropertyChildNode(sharpChartXMLNode, 'data.compare.symbols', getPerfChartSymbolsString(chartStore.perfChartSymbols));

  const useMainChartSymbolAsBaselinePropertyValue = chartStore.useMainChartSymbolAsBaseline ? 'perf' : 'pct';

  addPropertyChildNode(sharpChartXMLNode, 'data.compare.mode', useMainChartSymbolAsBaselinePropertyValue);

  const chartSizeMenuOption = getChartSizeObjFromChartSizeTitle(chartStore.chartSize);
  const chartWidth = chartSizeMenuOption === CHART_SIZE_MENU_OPTION_CUSTOM ? chartStore.customWidth : chartSizeMenuOption.width;
  const chartHeight = calculateSharpCharts2ChartHeight();

  addPropertyChildNode(sharpChartXMLNode, 'ui.chart.size', chartSizeMenuOption.sharpCharts2Id);
  addPropertyChildNode(sharpChartXMLNode, 'chart.width', chartWidth.toString());
  addPropertyChildNode(sharpChartXMLNode, 'chart.height', chartHeight.toString());

  if (colorScheme === COLOR_SCHEME_CUSTOM) {
    const customColorSchemeBackgroundColor = chartStore.customColorSchemeBackgroundColor.startsWith('#') ? chartStore.customColorSchemeBackgroundColor.slice(1): chartStore.customColorSchemeBackgroundColor;
    const customColorSchemeGridColor = chartStore.customColorSchemeGridColor.startsWith('#') ? chartStore.customColorSchemeGridColor.slice(1): chartStore.customColorSchemeGridColor;
    const customColorSchemeTextColor = chartStore.customColorSchemeTextColor.startsWith('#') ? chartStore.customColorSchemeTextColor.slice(1): chartStore.customColorSchemeTextColor;
    const customColorSchemeIndicatorColor = chartStore.customColorSchemeIndicatorColor.startsWith('#') ? chartStore.customColorSchemeIndicatorColor.slice(1): chartStore.customColorSchemeIndicatorColor;

    addPropertyChildNode(sharpChartXMLNode, 'custom.color.background', customColorSchemeBackgroundColor);
    addPropertyChildNode(sharpChartXMLNode, 'custom.color.grid', customColorSchemeGridColor);
    addPropertyChildNode(sharpChartXMLNode, 'custom.color.text', customColorSchemeTextColor);
    addPropertyChildNode(sharpChartXMLNode, 'custom.color.indicator', customColorSchemeIndicatorColor);
  }

  const { upColorSwatch, downColorSwatch, upColorRgb, downColorRgb } = getUpDownColorData(colorScheme);

  if (upColorRgb !== downColorRgb) {
    addPropertyChildNode(sharpChartXMLNode, 'palette.upPrice', upColorRgb);
    addPropertyChildNode(sharpChartXMLNode, 'palette.downPrice', downColorRgb);

    if (upColorSwatch !== 'auto' || downColorSwatch !== 'auto') {
      addPropertyChildNode(sharpChartXMLNode, 'palette.upVol', upColorRgb);
      addPropertyChildNode(sharpChartXMLNode, 'palette.downVol', downColorRgb);
    }
  }

  const chartGridOption = getChartGridObjFromChartGridTitle(chartStore.chartGrid);

  addPropertyChildNode(sharpChartXMLNode, 'grid.density.horiz', chartGridOption.sharpCharts2Id);
  addPropertyChildNode(sharpChartXMLNode, 'grid.density.vert', chartGridOption.sharpCharts2Id);

  if (chartGridOption.isDark && colorScheme !== COLOR_SCHEME_CUSTOM) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.alpha.lines.major.1', colorScheme.sharpCharts2GridAlphaLinesMajorOneValue);
    addPropertyChildNode(sharpChartXMLNode, 'grid.alpha.lines.major.2', colorScheme.sharpCharts2GridAlphaLinesMajorTwoValue);
    addPropertyChildNode(sharpChartXMLNode, 'grid.alpha.lines.minor', colorScheme.sharpCharts2GridAlphaLinesMinorValue);
  }

  const periodOption = getPeriodOptionFromPeriodValue(chartStore.period);
  const rangeOption = getRangeObjFromRangeTitle(chartStore.range);

  addPropertyChildNode(sharpChartXMLNode, 'data.type', periodOption.sharpCharts2Id);

  const rangeExtraBars = chartStore.rangeExtraBars === 0 ? '' : chartStore.rangeExtraBars.toString();

  addPropertyChildNode(sharpChartXMLNode, 'data.extraperiods', rangeExtraBars);

  const legendOption = LEGEND_OPTIONS_ARRAY.find(option => option.title === chartStore.legends);

  if (legendOption === LEGEND_OPTION_OFF) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'false');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'false');
  } else if (legendOption === LEGEND_OPTION_MINIMAL) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'price');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'false');
  } else if (legendOption === LEGEND_OPTION_VERBOSE) {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.companynames', 'true');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'true');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'true');
  } else {
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.layerTitles', 'true');
    addPropertyChildNode(sharpChartXMLNode, 'grid.show.overlayTitles', 'true');
  }

  addPropertyChildNode(sharpChartXMLNode, 'grid.show.streaminglabels', chartStore.yAxisLabels.toString());
  addPropertyChildNode(sharpChartXMLNode, 'chart.show.zoomthumbnail', chartStore.zoomThumbnail.toString());

  if (rangeOption.useBarAndGap) {
    addPropertyChildNode(sharpChartXMLNode, 'data.barWidth', chartStore.rangeBar);
    addPropertyChildNode(sharpChartXMLNode, 'data.gapWidth', chartStore.rangeGap);
  } else if (rangeOption.useNumberOfDays) {
    const numberOfDays = Number(chartStore.rangeDays) + (30 * Number(chartStore.rangeMonths)) + (365 * Number(chartStore.rangeYears));
    addPropertyChildNode(sharpChartXMLNode, 'data.days', numberOfDays.toString());
  } else if (rangeOption.useStartDateAndEndDate) {
    addPropertyChildNode(sharpChartXMLNode, 'data.start', chartStore.rangeStartDate);
    addPropertyChildNode(sharpChartXMLNode, 'data.end', chartStore.rangeEndDate);
  }

  const masterDataset = addNewChildNode(sharpChartXMLNode, DATASET);

  addAttribute(masterDataset, 'id', 'master');
  addAttribute(masterDataset, 'symbol', 'data.symbol');

  const sigDataset = addNewChildNode(sharpChartXMLNode, DATASET);

  addAttribute(sigDataset, 'id', 'sig');
  addAttribute(sigDataset, 'class', 'SIGCHG');
  addAttribute(sigDataset, 'args', '1,0');
  addAttribute(sigDataset, 'parent', 'price');

  const headerPanel = addNewChildNode(sharpChartXMLNode, PANEL);
  const headerLayer = addNewChildNode(headerPanel, LAYER);
  const headerDataset = addNewChildNode(headerLayer, DATASET);

  addAttribute(headerDataset, 'id', 'head');
  addAttribute(headerDataset, 'class', 'HEADER');
  addAttribute(headerDataset, 'parent', 'price');

  const performancePanel = addNewChildNode(sharpChartXMLNode, PANEL);

  addAttribute(performancePanel, 'height', '1.00');

  const performanceLayer = addNewChildNode(performancePanel, LAYER);
  const performanceScale = addNewChildNode(performanceLayer, SCALE);

  addAttribute(performanceScale, 'type', 'right_pct');

  const performanceDataset = addNewChildNode(performanceLayer, DATASET);

  addAttribute(performanceDataset, 'id', 'price');
  addAttribute(performanceDataset, 'symbol', 'data.symbol');
  addAttribute(performanceDataset, 'parent', 'master');

  const performanceStyle = addNewChildNode(performanceDataset, STYLE);

  addAttribute(performanceStyle, 'type', 'performance');
  addAttribute(performanceStyle, 'args', '1.5,solid');
  addAttribute(performanceStyle, 'swatchnumber', 'auto');

  const timescalePanel = addNewChildNode(sharpChartXMLNode, PANEL);
  const timescaleLayer = addNewChildNode(timescalePanel, LAYER);
  const timescaleDataset = addNewChildNode(timescaleLayer, DATASET);

  addAttribute(timescaleDataset, 'id', 'timebottom');
  addAttribute(timescaleDataset, 'class', 'TIMESCALE');
  addAttribute(timescaleDataset, 'parent', 'price');
}

const INDICATOR_LOCATION_ABOVE = 'above';
const INDICATOR_LOCATION_BEHIND = 'behind';
const INDICATOR_LOCATION_BELOW = 'below';

type IndicatorLocationId = typeof INDICATOR_LOCATION_ABOVE | typeof INDICATOR_LOCATION_BEHIND | typeof INDICATOR_LOCATION_BELOW;
const INDICATOR_LOCATION_IDS: IndicatorLocationId[] = [INDICATOR_LOCATION_ABOVE, INDICATOR_LOCATION_BEHIND, INDICATOR_LOCATION_BELOW];

const INDICATOR_LOCATIONS: IndicatorPosition[] = [INDICATOR_POSITION_ABOVE, INDICATOR_POSITION_BEHIND_PRICE, INDICATOR_POSITION_BELOW];

interface IndicatorPanel {
  indicators: Indicator[];
};

interface IndicatorLocation {
  location: IndicatorPosition,
  locationId: IndicatorLocationId;
  panels: IndicatorPanel[]
};

function addIndicatorsAndOverlaysToSharpChartXMLNode(sharpChartXMLNode: XmlNode, colorScheme: ColorScheme): void {
  const chartStore = useChartStore();
  const sortedIndicatorsArray = chartStore.sortedIndicators.filter(i => i?.indicatorName && i.indicatorName !== NONE);
  const sortedOverlaysArray = chartStore.sortedOverlays.filter(o => o?.overlayName && o.overlayName !== NONE);
  const chartPanels = [];
  const pricePanel = createNode(PANEL);
  const pricePanelMainLayer = addNewChildNode(pricePanel, LAYER);
  const pricePanelMainScale = addNewChildNode(pricePanelMainLayer, SCALE);
  const pricePanelMainDataset = addNewChildNode(pricePanelMainLayer, DATASET);
  const pricePanelMainStyle = addNewChildNode(pricePanelMainDataset, STYLE);

  addAttribute(pricePanel, 'height', '1.00');

  const pricePanelMainScaleTypeValue = chartStore.chartType === CHART_TYPE_OPTION_PERFORMANCE_PERCENT.value ? 'right_pct' : 'right';
  const pricePanelMainScaleLogValue = chartStore.isLogScale ? 'true' : '';

  addAttribute(pricePanelMainScale, 'type', pricePanelMainScaleTypeValue);
  addAttribute(pricePanelMainScale, 'log', pricePanelMainScaleLogValue);

  addAttribute(pricePanelMainDataset, 'id', 'price');
  addAttribute(pricePanelMainDataset, 'symbol', 'data.symbol');
  addAttribute(pricePanelMainDataset, 'parent', 'master');

  const chartTypeOption = getChartTypeObjFromChartTypeValue(chartStore.chartType);

  addAttribute(pricePanelMainStyle, 'type', chartTypeOption.sharpCharts2TypeValue);

  if (chartTypeOption.sharpCharts2ArgsValue) {
    addAttribute(pricePanelMainStyle, 'args', chartTypeOption.sharpCharts2ArgsValue);
  } else if (chartTypeOption.sharpCharts2IsCandleBased && chartStore.solidCandles) {
    addAttribute(pricePanelMainStyle, 'args', 'solid');
  }

  const { upColorSwatch, upColorRgb, downColorRgb } = getUpDownColorData(colorScheme);
  const signalValue = upColorRgb === downColorRgb ? '' : 'sig';
  const swatchNumberValue = upColorRgb === downColorRgb ? upColorSwatch : '';
  const opacityOption = getOpacityObjFromOpacityTitle(chartStore.opacity);

  addAttribute(pricePanelMainStyle, 'swatchnumber', swatchNumberValue);
  addAttribute(pricePanelMainStyle, 'alpha', opacityOption.sharpCharts2AlphaValue);
  addAttribute(pricePanelMainStyle, 'signal', signalValue);

  if (chartStore.priceLabels) {
    const pricePanelPriceLabelsDataset = addNewChildNode(pricePanelMainLayer, DATASET);

    addAttribute(pricePanelPriceLabelsDataset, 'id', 'labelOver');
    addAttribute(pricePanelPriceLabelsDataset, 'class', PRICE_LABELS.sharpCharts2Id);
    addAttribute(pricePanelPriceLabelsDataset, 'parent', 'price');
  }

  for (let i = 0; i < sortedOverlaysArray.length; i++) {
    const overlay = sortedOverlaysArray[i];
    const overlayDefinition = getIndicatorOrOverlayObjFromIndicatorOrOverlayName(overlay.overlayName);
    const overlayDataset = addNewChildNode(pricePanelMainLayer, DATASET);
    const overlayStyle = addNewChildNode(overlayDataset, STYLE);

    addAttribute(overlayDataset, 'id', `over${i + 1}`);
    addAttribute(overlayDataset, 'class', overlayDefinition.sharpCharts2Id);

    let overlayParameters = overlay.parameters;

    if (overlayDefinition === PRICE_SAME_SCALE) {
      overlayParameters = replaceSymbolWithString(overlayParameters, chartStore.symbol, 'data.symbol');

      addAttribute(overlayDataset, 'symbol', overlayParameters);
    }

    addAttribute(overlayDataset, 'args', overlayParameters);

    const parent = overlayDefinition === PRICE_SAME_SCALE ? 'master' : 'price';

    addAttribute(overlayDataset, 'parent', parent);

    const overlayStyleObj = getOverlayStyleObjFromOverlayStyleTitle(overlay.overlayStyle);
    const swatchIndex = colorScheme.sharpCharts2OverlaySwatches.findIndex(swatch => swatch.upDownColorOption === overlay.overlayColor);
    const opacityObj = getOpacityObjFromOpacityTitle(overlay.overlayOpacity);
    let overlayStyleElement = '';
    let overlayStyleType = overlayStyleObj.sharpCharts2TypeId;
    let overlayStyleArgs = overlayStyleObj.sharpCharts2Args;
    let overlayStyleSwatchNumber = swatchIndex === -1 ? 'auto' : `${swatchIndex}`;
    let overlayStyleOpacity = opacityObj.sharpCharts2AlphaValue;
    let overlayStyleSignal = '';

    if (overlayDefinition === ICHIMOKU_CLOUD || overlayDefinition === ICHIMOKU_CLOUD_FULL) {
      overlayStyleElement = 'ichimoku cloud';
      overlayStyleType = OVERLAY_STYLE_AREA.sharpCharts2TypeId;
      overlayStyleArgs = OVERLAY_STYLE_AREA.sharpCharts2Args;
    }

    if (overlayDefinition === VOLUME_BY_PRICE) {
      overlayStyleType = 'hist';
      overlayStyleArgs = '';
      overlayStyleSwatchNumber = '';

      if (!overlayStyleOpacity) {
        overlayStyleOpacity = OPACITY_OPTION_ZERO_POINT_FIVE.sharpCharts2AlphaValue;
      }

      if (chartStore.colorVolume) {
        overlayStyleSignal = 'sig';
      }
    }

    addAttribute(overlayStyle, 'element', overlayStyleElement);
    addAttribute(overlayStyle, 'type', overlayStyleType);
    addAttribute(overlayStyle, 'args', overlayStyleArgs);
    addAttribute(overlayStyle, 'swatchnumber', overlayStyleSwatchNumber);
    addAttribute(overlayStyle, 'alpha', overlayStyleOpacity);
    addAttribute(overlayStyle, 'signal', overlayStyleSignal);
  }

  if (chartStore.chartSettingsVolumeMenuValue === CHART_SETTINGS_VOLUME_MENU_OPTION_OVERLAY) {
    const volumeOverlayLayer = addNewChildNode(pricePanel, LAYER);
    const volumeOverlayScale = addNewChildNode(volumeOverlayLayer, SCALE);
    const volumeOverlayDataset = addNewChildNode(volumeOverlayLayer, DATASET);

    addAttribute(volumeOverlayScale, 'type', 'left');
    addAttribute(volumeOverlayDataset, 'id', 'volUnder');
    addAttribute(volumeOverlayDataset, 'class', VOLUME.sharpCharts2Id);
    addAttribute(volumeOverlayDataset, 'parent', 'price');

    if (chartStore.colorVolume) {
      const volumeOverlayStyle = addNewChildNode(volumeOverlayDataset, STYLE);

      addAttribute(volumeOverlayStyle, 'type', 'hist');
      addAttribute(volumeOverlayStyle, 'signal', 'sig');
    }
  }

  const firstBehindPricePositionedIndicatorIndex = sortedIndicatorsArray.findIndex(i => i.indicatorPosition === INDICATOR_POSITION_BEHIND_PRICE.title);
  const firstBelowPricePositionedIndicatorIndex = sortedIndicatorsArray.findIndex(i => i.indicatorPosition === INDICATOR_POSITION_BELOW.title);
  let indicatorsPositionedAbovePrice: Indicator[] = [];
  let indicatorsPositionedBehindPrice: Indicator[] = [];
  let indicatorsPositionedBelowPrice: Indicator[] = [];

  if (firstBehindPricePositionedIndicatorIndex === -1 && firstBelowPricePositionedIndicatorIndex === -1) {
    indicatorsPositionedAbovePrice = sortedIndicatorsArray;
  } else if (firstBehindPricePositionedIndicatorIndex === -1) {
    indicatorsPositionedAbovePrice = sortedIndicatorsArray.slice(0, firstBelowPricePositionedIndicatorIndex);
    indicatorsPositionedBelowPrice = sortedIndicatorsArray.slice(firstBelowPricePositionedIndicatorIndex);
  } else if (firstBelowPricePositionedIndicatorIndex === -1) {
    indicatorsPositionedAbovePrice = sortedIndicatorsArray.slice(0, firstBehindPricePositionedIndicatorIndex);
    indicatorsPositionedBehindPrice = sortedIndicatorsArray.slice(firstBehindPricePositionedIndicatorIndex);
  } else {
    indicatorsPositionedAbovePrice = sortedIndicatorsArray.slice(0, firstBehindPricePositionedIndicatorIndex);
    indicatorsPositionedBehindPrice = sortedIndicatorsArray.slice(firstBehindPricePositionedIndicatorIndex, firstBelowPricePositionedIndicatorIndex);
    indicatorsPositionedBelowPrice = sortedIndicatorsArray.slice(firstBelowPricePositionedIndicatorIndex);
  }

  const unformattedIndicatorLocations = [indicatorsPositionedAbovePrice, indicatorsPositionedBehindPrice, indicatorsPositionedBelowPrice];
  const indicatorLocations: IndicatorLocation[] = [];

  for (let i = 0; i < unformattedIndicatorLocations.length; i++) {
    const location = INDICATOR_LOCATIONS[i];
    const locationId = INDICATOR_LOCATION_IDS[i];
    const formattedIndicatorsByLocation: IndicatorLocation = {
      location,
      locationId,
      panels: []
    };

    let currentIndicatorPanel: IndicatorPanel | undefined = undefined;

    for (let j = 0; j < unformattedIndicatorLocations[i].length; j++) {
      const indicator = unformattedIndicatorLocations[i][j];

      if (
        indicator.indicatorPosition !== INDICATOR_POSITION_BEHIND_INDICATOR.title &&
        (j === 0 || location.title !== INDICATOR_POSITION_BEHIND_PRICE.title)
      ) {
        currentIndicatorPanel = {
          indicators: []
        };

        formattedIndicatorsByLocation.panels.push(currentIndicatorPanel);
      }

      if (currentIndicatorPanel) {
        currentIndicatorPanel.indicators.push(indicator);
      }
    }

    indicatorLocations.push(formattedIndicatorsByLocation);
  }

  let indicatorNum = 1;

  for (const indicatorLocation of indicatorLocations) {
    const panels = indicatorLocation.panels;
    const location = indicatorLocation.location;
    const locationId = indicatorLocation.locationId;
    const isPricePanelLocation = location.title === INDICATOR_POSITION_BEHIND_PRICE.title;

    if (isPricePanelLocation) {
      chartPanels.push(pricePanel);
    }

    for (const panel of panels) {
      const indicators = panel.indicators;
      const xmlPanel = isPricePanelLocation ? pricePanel : createNode(PANEL);

      if (!isPricePanelLocation) {
        chartPanels.push(xmlPanel);
      }

      let isRightYAxisClaimed = isPricePanelLocation;
      let performanceYAxis = isPricePanelLocation && chartStore.chartType === CHART_TYPE_OPTION_PERFORMANCE_PERCENT.value ? 'right_pct' : '';
      let isLeftYAxisClaimed = false;

      if (isPricePanelLocation && chartStore.chartSettingsVolumeMenuValue === CHART_SETTINGS_VOLUME_MENU_OPTION_OVERLAY) {
        isLeftYAxisClaimed = true;
      }

      let doesFirstIndicatorOnPanelHaveOverlayWithItsOwnScale = false;

      for (let i = 0; i < indicators.length; i++) {
        const indicator = indicators[i];
        const indicatorId = `${locationId}${indicatorNum}`;
        const isFirstIndicatorOnPanel = i === 0;
        const isDateTimeAxisIndicator = indicator.indicatorName === DATE_TIME_AXIS.name;
        const isVolumeIndicator = indicator.indicatorName === VOLUME.name;
        const isPriceIndicator = indicator.indicatorName === PRICE.name;
        const isPricePerformanceIndicator = indicator.indicatorName === PRICE_PERFORMANCE.name;
        const layer = addNewChildNode(xmlPanel, LAYER);
        const opacityObj = getOpacityObjFromOpacityTitle(indicator.indicatorOpacity);
        const opacityValue = isDateTimeAxisIndicator ? '' : opacityObj.sharpCharts2AlphaValue;
        let indicatorSwatchIndex = colorScheme.sharpCharts2OverlaySwatches.findIndex(swatch => swatch.upDownColorOption === indicator.indicatorColor).toString();

        if (indicatorSwatchIndex === '-1') {
          indicatorSwatchIndex = 'auto';
        } else {
          indicatorSwatchIndex = `${indicatorSwatchIndex}`;
        }

        addAttribute(layer, 'alpha', opacityValue);

        const indicatorOption = getIndicatorOrOverlayObjFromIndicatorOrOverlayName(indicator.indicatorName);
        const overlayOption = indicator.overlayName && indicator.overlayName !== NONE ? getIndicatorOrOverlayObjFromIndicatorOrOverlayName(indicator.overlayName) : null;

        if (isFirstIndicatorOnPanel && overlayOption) {
          doesFirstIndicatorOnPanelHaveOverlayWithItsOwnScale = overlayOption.shouldShareScaleWithParent;
        }

        const scale = addNewChildNode(layer, SCALE);
        let scaleType;

        if (isPricePerformanceIndicator) {
          if (performanceYAxis) {
            scaleType = performanceYAxis;
          } else if (isFirstIndicatorOnPanel) {
            if (isPricePanelLocation) {
              scaleType = 'left_pct';
              isLeftYAxisClaimed = true;
            } else {
              scaleType = 'right_pct';
              isRightYAxisClaimed = true;
            }
          } else {
            if (!doesFirstIndicatorOnPanelHaveOverlayWithItsOwnScale || isPricePanelLocation) {
              scaleType = 'left_pct';
              isLeftYAxisClaimed = true;
            } else {
              scaleType = 'none';
            }
          }

          performanceYAxis = scaleType;
        } else {
          if (isRightYAxisClaimed && isLeftYAxisClaimed) {
            scaleType = isPricePanelLocation ? 'noshow' : 'none';
          } else if (isRightYAxisClaimed) {
            scaleType = 'left';
            isLeftYAxisClaimed = true;
          } else {
            scaleType = 'right';
            isRightYAxisClaimed = true;
          }
        }

        addAttribute(scale, 'type', scaleType);

        let indicatorHeight = '';

        if (!isDateTimeAxisIndicator) {
          const heightOption = getIndicatorHeightObjFromIndicatorHeightTitle(indicator.indicatorHeight);
          indicatorHeight = heightOption.sharpCharts2Id;
        }

        if (isFirstIndicatorOnPanel && !isPricePanelLocation) {
          addAttribute(xmlPanel, 'height', indicatorHeight);
        } else {
          addAttribute(scale, 'height', indicatorHeight);
        }

        if (isVolumeIndicator) {
          addAttribute(scale, 'anchor', 'bottom');
        } else if (isPricePanelLocation && !isDateTimeAxisIndicator) {
          addAttribute(scale, 'anchor', 'center');
        }

        let indicatorParameters = indicator.parameters;

        if (
          (isPriceIndicator || isPricePerformanceIndicator) &&
          indicatorParameters.endsWith(',inverted')
        ) {
          addAttribute(scale, 'inverted', 'true');

          indicatorParameters = indicatorParameters.slice(0, indicatorParameters.length - 9);
        }

        if (
          (isPriceIndicator || isPricePerformanceIndicator) &&
          indicatorParameters.endsWith(',log')
        ) {
          addAttribute(scale, 'log', 'true');

          indicatorParameters = indicatorParameters.slice(0, indicatorParameters.length - 4);
        }

        const dataset = addNewChildNode(layer, DATASET);

        addAttribute(dataset, 'id', indicatorId);
        addAttribute(dataset, 'class', indicatorOption.sharpCharts2Id);

        let parentValue = 'price';

        if (isPriceIndicator || isPricePerformanceIndicator) {
          indicatorParameters = replaceSymbolWithString(indicatorParameters, chartStore.symbol, 'data.symbol');

          addAttribute(dataset, 'symbol', indicatorParameters);

          parentValue = 'master';
        }

        let argsValue = indicatorParameters;

        if (
          isDateTimeAxisIndicator ||
          (isVolumeIndicator && !indicatorParameters)
        ) {
          argsValue = 'user-defined';
        }

        addAttribute(dataset, 'args', argsValue);
        addAttribute(dataset, 'parent', parentValue);

        if (!isDateTimeAxisIndicator) {
          const style = addNewChildNode(dataset, STYLE);

          if (isPriceIndicator || isPricePerformanceIndicator) {
            const priceIndicatorStyleOption = getPriceIndicatorStyleObjFromPriceIndicatorStyleTitle(indicator.indicatorPriceStyle);

            addAttribute(style, 'type', priceIndicatorStyleOption.sharpCharts2StyleType);
            addAttribute(style, 'args', priceIndicatorStyleOption.sharpCharts2StyleArgs);
          }

          if (isVolumeIndicator) {
            const additionalVolumeStyle = addNewChildNode(dataset, STYLE);

            addAttribute(additionalVolumeStyle, 'element', 'signal');
            addAttribute(additionalVolumeStyle, 'type', 'signal1');
            addAttribute(additionalVolumeStyle, 'args', '1.0,solid');
            addAttribute(additionalVolumeStyle, 'swatchnumber', '0');

            if (chartStore.colorVolume) {
              addAttribute(style, 'type', 'hist');
              addAttribute(style, 'signal', 'sig');
            }
          }

          addAttribute(style, 'swatchnumber', indicatorSwatchIndex);

          if (isPriceIndicator || isPricePerformanceIndicator || isPricePanelLocation) {
            addAttribute(style, 'alpha', opacityValue);
          }
        }

        if (overlayOption) {
          let overlayDataset;

          if (overlayOption.shouldShareScaleWithParent) {
            overlayDataset = addNewChildNode(layer, DATASET);
          } else {
            const overlayLayer = addNewChildNode(xmlPanel, LAYER);
            const overlayScale = addNewChildNode(overlayLayer, SCALE);
            let overlayScaleType;

            if (isRightYAxisClaimed && isLeftYAxisClaimed) {
              overlayScaleType = isPricePanelLocation ? 'noshow' : 'none';
            } else if (isRightYAxisClaimed) {
              overlayScaleType = 'left';
              isLeftYAxisClaimed = true;
            } else {
              overlayScaleType = 'right';
              isRightYAxisClaimed = true;
            }

            addAttribute(overlayScale, 'type', overlayScaleType);
            addAttribute(overlayScale, 'height', indicatorHeight);
            addAttribute(overlayScale, 'anchor', 'center');

            overlayDataset = addNewChildNode(overlayLayer, DATASET);
          }

          const overlayId = `${indicatorId}o`;

          addAttribute(overlayDataset, 'id', overlayId);
          addAttribute(overlayDataset, 'class', overlayOption.sharpCharts2Id);
          addAttribute(overlayDataset, 'args', indicator.overlayParameters);
          addAttribute(overlayDataset, 'parent', indicatorId);
        }

        indicatorNum++;
      }
    }
  }

  const dateBarPanelDirectlyUnderPricePanel = createNode(PANEL);
  const dateBarLayerDirectlyUnderPricePanel = addNewChildNode(dateBarPanelDirectlyUnderPricePanel, LAYER);
  const dateBarDatasetDirectlyUnderPricePanel = addNewChildNode(dateBarLayerDirectlyUnderPricePanel, DATASET);
  const dateBarPanelAtTheBottomOfChart = createNode(PANEL);
  const dateBarLayerAtTheBottomOfChart = addNewChildNode(dateBarPanelAtTheBottomOfChart, LAYER);
  const dateBarDatasetAtTheBottomOfChart = addNewChildNode(dateBarLayerAtTheBottomOfChart, DATASET);

  addAttribute(dateBarDatasetDirectlyUnderPricePanel, 'id', 'timebelow');
  addAttribute(dateBarDatasetDirectlyUnderPricePanel, 'class', DATE_TIME_AXIS.sharpCharts2Id);
  addAttribute(dateBarDatasetDirectlyUnderPricePanel, 'parent', 'price');
  addAttribute(dateBarDatasetAtTheBottomOfChart, 'id', 'timebottom');
  addAttribute(dateBarDatasetAtTheBottomOfChart, 'class', DATE_TIME_AXIS.sharpCharts2Id);
  addAttribute(dateBarDatasetAtTheBottomOfChart, 'parent', 'price');

  const pricePanelIndex = chartPanels.indexOf(pricePanel);
  let isLastPanelOnChartThePricePanel = pricePanelIndex === chartPanels.length - 1;

  if (chartStore.chartSettingsVolumeMenuValue === CHART_SETTINGS_VOLUME_MENU_OPTION_SEPARATE) {
    const separateVolumeIndicatorPanelDirectlyUnderPricePanel = createNode(PANEL);
    const separateVolumeIndicatorLayerDirectlyUnderPricePanel = addNewChildNode(separateVolumeIndicatorPanelDirectlyUnderPricePanel, LAYER);
    const separateVolumeIndicatorScaleDirectlyUnderPricePanel = addNewChildNode(separateVolumeIndicatorLayerDirectlyUnderPricePanel, SCALE);
    const separateVolumeIndicatorDatasetDirectlyUnderPricePanel = addNewChildNode(separateVolumeIndicatorLayerDirectlyUnderPricePanel, DATASET);

    addAttribute(separateVolumeIndicatorLayerDirectlyUnderPricePanel, 'alpha', '1.0');
    addAttribute(separateVolumeIndicatorScaleDirectlyUnderPricePanel, 'type', 'right');
    addAttribute(separateVolumeIndicatorDatasetDirectlyUnderPricePanel, 'id', 'volBelow');
    addAttribute(separateVolumeIndicatorDatasetDirectlyUnderPricePanel, 'class', VOLUME.sharpCharts2Id);
    addAttribute(separateVolumeIndicatorDatasetDirectlyUnderPricePanel, 'parent', 'price');

    if (chartStore.colorVolume) {
      const separateVolumeIndicatorStyleDirectlyUnderPricePanel = addNewChildNode(separateVolumeIndicatorDatasetDirectlyUnderPricePanel, STYLE);

      addAttribute(separateVolumeIndicatorStyleDirectlyUnderPricePanel, 'type', 'hist');
      addAttribute(separateVolumeIndicatorStyleDirectlyUnderPricePanel, 'signal', 'sig');
    }

    if (isLastPanelOnChartThePricePanel) {
      chartPanels.push(separateVolumeIndicatorPanelDirectlyUnderPricePanel);
    } else {
      chartPanels.splice(pricePanelIndex + 1, 0, separateVolumeIndicatorPanelDirectlyUnderPricePanel);
    }

    isLastPanelOnChartThePricePanel = false;
  }

  if (isLastPanelOnChartThePricePanel) {
    if (
      !chartPanels[chartPanels.length - 2] ||
      !chartPanels[chartPanels.length - 2].children[0] ||
      chartPanels[chartPanels.length - 2].children[0].type !== LAYER ||
      !chartPanels[chartPanels.length - 2].children[0].children[1] ||
      chartPanels[chartPanels.length - 2].children[0].children[1].type !== DATASET ||
      !chartPanels[chartPanels.length - 2].children[0].children[1].attributes.find(attribute => attribute.key === 'class' && attribute.value === DATE_TIME_AXIS.sharpCharts2Id)
    ) {
      chartPanels.push(dateBarPanelAtTheBottomOfChart);
    }
  } else {
    chartPanels.splice(pricePanelIndex + 1, 0, dateBarPanelDirectlyUnderPricePanel);

    const isLastPanelOnChartADateBar =
      !!chartPanels[chartPanels.length - 1].children[0] &&
      chartPanels[chartPanels.length - 1].children[0].type === LAYER &&
      (
        (
          !!chartPanels[chartPanels.length - 1].children[0].children[0] &&
          chartPanels[chartPanels.length - 1].children[0].children[0].type === DATASET &&
          !!chartPanels[chartPanels.length - 1].children[0].children[0].attributes.find(attribute => attribute.key === 'class' && attribute.value === DATE_TIME_AXIS.sharpCharts2Id)
        ) ||
        (
          !!chartPanels[chartPanels.length - 1].children[0].children[1] &&
          chartPanels[chartPanels.length - 1].children[0].children[1].type === DATASET &&
          !!chartPanels[chartPanels.length - 1].children[0].children[1].attributes.find(attribute => attribute.key === 'class' && attribute.value === DATE_TIME_AXIS.sharpCharts2Id)
        )
      )

    if (!isLastPanelOnChartADateBar) {
      chartPanels.push(dateBarPanelAtTheBottomOfChart);
    }
  }

  for (const chartPanel of chartPanels) {
    addExistingChildNode(sharpChartXMLNode, chartPanel);
  }
}

function stringifyXMLNode(node: XmlNode): string {
  let xml = `<${node.type}`;

  for (const attribute of node.attributes) {
    xml += ` ${attribute.key}="${attribute.value}"`;
  }

  if (node.children.length) {
    xml += '>';

    for (const child of node.children) {
      xml += stringifyXMLNode(child);
    }

    xml += `</${node.type}>`;
  } else {
    xml += '/>';
  }

  return xml;
}

export function convertChartDataToXML(): Omit<SharpCharts2ChartImageUrlData, 'i'> {
  const chartStore = useChartStore();
  const { getPerfChartSymbolsString } = usePerfChartSymbols();

  const sharpChartXMLNode = createNode(SHARPCHART);
  const colorScheme = getColorSchemeObjFromColorSchemeValue(chartStore.colorScheme);

  if (chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value) {
    addPerfChartSettingsToSharpChartXMLNode(sharpChartXMLNode, colorScheme);
  } else {
    addChartSettingsToSharpChartXMLNode(sharpChartXMLNode, colorScheme);
    addIndicatorsAndOverlaysToSharpChartXMLNode(sharpChartXMLNode, colorScheme);
  }

  return {
    chartXML: stringifyXMLNode(sharpChartXMLNode),
    symbol: chartStore.symbol,
    period: chartStore.period,
    chartType: chartStore.chartType,
    range: chartStore.range,
    rangeBar: chartStore.rangeBar,
    rangeGap: chartStore.rangeGap,
    rangeYears: chartStore.rangeYears,
    rangeMonths: chartStore.rangeMonths,
    rangeDays: chartStore.rangeDays,
    rangeStartDate: chartStore.rangeStartDate,
    rangeEndDate: chartStore.rangeEndDate,
    perfChartSymbolsString: getPerfChartSymbolsString(chartStore.perfChartSymbols),
    useMainChartSymbolAsBaseline: chartStore.useMainChartSymbolAsBaseline,
    annotationsRecid: chartStore.recidOfChartBeingViewed
  };
}
